import type { KyInstance, Options } from 'ky';
import type { SvpAsset, SvpAssetId } from '@vgtv/api-client/lib/svp_asset';
import type {
  SvpCategory,
  SvpCategoryId,
} from '@vgtv/api-client/lib/svp_category';
import type { UnixTimestamp } from '@vgtv/api-client';

import type { Vendor } from '../../types';
import { isTruthy } from '../../utils/types';

import { SvpFilters } from './filters';

export interface SvpSeason {
  title: string;
  seasonNumber: number;
}

type HalEmbedded<T extends SvpAsset | SvpCategory | SvpSeason> =
  T extends SvpAsset
    ? { assets: T[] }
    : T extends SvpCategory
      ? { categories: T[] }
      : T extends SvpSeason
        ? { seasons: T[] }
        : never;

interface HalLink {
  _links: {
    self: {
      href: string;
    };
    next?: {
      href: string;
    };
  };
}

export interface HalResult<T extends SvpAsset | SvpCategory | SvpSeason>
  extends HalLink {
  _embedded: HalEmbedded<T>;
  total_items: number;
}

export function parseParamsFromUrl<
  T =
    | GetAssetArgs
    | GetAssetsArgs
    | SearchAssetsArgs
    | GetMostSeenAssetsArgs
    | GetSeasonAssetsArgs
    | GetCategoryArgs
    | GetCategoriesArgs,
>(url: string = '') {
  const entries = new URL(url).searchParams.entries();

  const params: Record<
    string,
    string | boolean | number | SvpFilters | undefined
  > = {};
  for (let [key, val] of entries) {
    if (key === 'additional' && ['true', 'false'].includes(val)) {
      // getCategories method expects an 'additional' argument of type boolean
      params.additional = val === 'true';
    } else if (['page', 'limit', 'before', 'after'].includes(key)) {
      // various numeric fields
      params[key] = val ? parseInt(val, 10) : undefined;
    } else if (key === 'filter') {
      params.filters = SvpFilters.from(val);
    } else {
      // all other string-based params
      params[key] = val;
    }
  }

  return params as unknown as T;
}

interface CommonArgs {
  vendor: Vendor;
}

export interface GetAssetArgs extends CommonArgs {
  assetId: SvpAssetId;
  additional?: string;
}

function createGetAsset(client: KyInstance, appName: string) {
  return async function getAsset<T extends SvpAsset = SvpAsset>(
    { assetId, vendor, additional }: GetAssetArgs,
    options?: Options
  ) {
    const searchParams = new URLSearchParams({ appName });
    if (additional) {
      searchParams.set('additional', additional);
    }

    return client
      .get(`${vendor}/assets/${assetId}`, {
        ...options,
        searchParams,
      })
      .json<T>();
  };
}

export interface GetAssetsArgs extends CommonArgs {
  /**
   * Available fields:
   * created, updated, published
   *
   * Example:
   * published|-created
   */
  sort?: string;

  /**
   * Default: 10
   * Max: 100
   */
  limit?: number;

  /**
   * Page token returned by the previous API request that enables you to page through results.
   * Value of this field is presented in the "next" link of the response body.
   */
  page?: number;

  /**
   * Filter list in the format of key::value. Each filter has to be separated by | sign.
   *
   * Defaults:
   * By default, this endpoint returns active assets of video type.
   * If you want to fetch assets regardless of its type you need to apply assetType::any filter.
   *
   * Available filters:
   * categoryId, externalCategoryId, streamType, status, assetId, originAssetId, tag, story, assetType
   *
   * Behaviour: Specifying multiple filters results in logical AND between them.
   * Filters that specify the list (e.g. assetId) results in logical OR between list items.
   *
   * Unsupported filters are ignored and do not break the request.
   * Unknown filter values are ignored unless all of them are invalid so the whole filter is ignored.
   *
   * categoryId filter allows to get assets that belong to chosen category and all the children subcategories.
   *
   * Example:
   * categoryId::139|streamType::vod|status::active|assetId::2
   *
   * List example:
   * status::active,inactive,draft|assetId::2,52,56|tag::foo,bar
   */
  filters?: SvpFilters;

  /**
   * Additional fields:
   * settings, externalId, externalCategoryId, chapters, featuredChapters, url,
   * cuePoints, imageCaption, nextAsset, subtitles, sourceFiles, tags, stories, access, metadata
   */
  additional?: string;

  /**
   * By default, only assets with flag showInNewest set to true are returned.
   * By providing this parameter it is possible to omit this restriction.
   */
  showAll?: boolean;

  /**
   * Unix timestamp. Limits assets to the ones with creation date older or equal than this value.
   *
   * By default the value of this field is compared against created timestamp.
   * When sort parameter is present, the field works either on created, updated or published field,
   * depending on which one appears first in sort
   *
   * Example:
   * &sort=updated&before=123 - assets that update timestamp is lower or equal to 123
   */
  before?: UnixTimestamp;

  /**
   * Unix timestamp. Limits assets to the ones with creation date newer or equal than this value.
   *
   * By default the value of this field is compared against created timestamp. When sort parameter is present, the field works either on created, updated or published field, depending on which one appears first in sort
   *
   * Example:
   * &sort=updated&after=123 - assets that update timestamp is greater or equal to 123
   */
  after?: UnixTimestamp;
}

function createGetAssets(client: KyInstance, appName: string) {
  return async function getAssets<T extends SvpAsset = SvpAsset>(
    {
      vendor,
      sort,
      limit,
      page,
      filters,
      additional,
      showAll,
      before,
      after,
    }: GetAssetsArgs,
    options?: Options
  ) {
    const searchParams = new URLSearchParams({ appName });

    if (sort) {
      searchParams.set('sort', sort);
    }

    if (limit) {
      searchParams.set('limit', limit.toString(10));
    }

    if (page) {
      searchParams.set('page', page.toString(10));
    }

    if (filters && !filters.isEmpty()) {
      searchParams.set('filter', filters.toString());
    }

    if (additional) {
      searchParams.set('additional', additional);
    }

    if (showAll) {
      searchParams.set('showAll', showAll ? 'true' : 'false');
    }

    if (before) {
      searchParams.set('before', before.toString(10));
    }

    if (after) {
      searchParams.set('after', after.toString(10));
    }

    return await client
      .get(`${vendor}/assets`, {
        ...options,
        searchParams,
      })
      .json<HalResult<T>>();
  };
}

export interface SearchAssetsArgs extends CommonArgs {
  /**
   * Phrase to search for in some fields of an asset.
   * See Sifter query documentation for details.
   * @see https://github.schibsted.io/svp/sifter/blob/master/docs/params.md#query
   */
  query?: string;

  /**
   * Available fields:
   * created, updated, published, flightTimes.start
   *
   * Example:
   * published|-created
   */
  sort?: string;

  /**
   * Default: 10
   * Max: 100
   */
  limit?: number;

  /**
   * Page token returned by the previous API request that enables you to page through results.
   * Value of this field is presented in the "next" link of the response body.
   */
  page?: number;

  /**
   * Filter list in the format of key::value. Each filter has to be separated by | sign.
   *
   * Defaults:
   * By default, this endpoint returns active assets of video type.
   * If you want to fetch assets regardless of its type you need to apply assetType::any filter.
   *
   * Available filters:
   * categoryId, externalCategoryId, streamType, status, assetId, originAssetId, tag, story, assetType
   *
   * Behaviour: Specifying multiple filters results in logical AND between them.
   * Filters that specify the list (e.g. assetId) results in logical OR between list items.
   *
   * Invalid and unsupported filters result in 400 Bad Request.
   *
   * categoryId filter allows to get assets that belong to chosen category and all the children subcategories.
   *
   * Example:
   * categoryId::139|streamType::vod|status::active|assetId::2
   *
   * List example:
   * status::active,inactive,draft|assetId::2,52,56|tag::foo,bar
   */
  filters?: SvpFilters;

  /**
   * Additional fields:
   * settings, externalId, externalCategoryId, chapters, featuredChapters, url,
   * cuePoints, imageCaption, nextAsset, subtitles, sourceFiles, tags, stories, access, metadata
   */
  additional?: string;

  /**
   * Unix timestamp. Limits assets to the ones with creation date older or equal than this value.
   *
   * By default the value of this field is compared against created timestamp.
   * When sort parameter is present, the field works either on created, updated or published field,
   * depending on which one appears first in sort
   *
   * Example:
   * &sort=updated&before=123 - assets that update timestamp is lower or equal to 123
   */
  before?: UnixTimestamp;

  /**
   * Unix timestamp. Limits assets to the ones with creation date newer or equal than this value.
   *
   * By default the value of this field is compared against created timestamp. When sort parameter is present, the field works either on created, updated or published field, depending on which one appears first in sort
   *
   * Example:
   * &sort=updated&after=123 - assets that update timestamp is greater or equal to 123
   */
  after?: UnixTimestamp;
}

function createSearchAssets(client: KyInstance, appName: string) {
  return async function searchAssets<T extends SvpAsset = SvpAsset>(
    params: SearchAssetsArgs,
    options?: Options
  ) {
    const {
      vendor,
      sort,
      limit,
      page,
      filters,
      additional,
      before,
      after,
      query,
    } = params;
    const searchParams = new URLSearchParams({ appName });

    if (sort) {
      searchParams.set('sort', sort);
    }

    if (limit) {
      searchParams.set('limit', limit.toString(10));
    }

    if (page) {
      searchParams.set('page', page.toString(10));
    }

    if (filters && !filters.isEmpty()) {
      searchParams.set('filter', filters.toString());
    }

    if (additional) {
      searchParams.set('additional', additional);
    }

    if (query) {
      searchParams.set('query', query);
    }

    if (before) {
      searchParams.set('before', before.toString(10));
    }

    if (after) {
      searchParams.set('after', after.toString(10));
    }

    return await client
      .get(`${vendor}/search`, {
        ...options,
        searchParams,
      })
      .json<HalResult<T>>();
  };
}

export interface GetMostSeenAssetsArgs extends CommonArgs {
  /**
   * Default: 10
   * Max: 100
   */
  limit?: number;

  /**
   * Page token returned by the previous API request that enables you to page through results.
   * Value of this field is presented in the "next" link of the response body.
   */
  page?: number;

  /**
   * Filter list in the format of key::value. Each filter has to be separated by | sign.
   *
   * Defaults:
   * By default, this endpoint returns active assets of video type.
   * If you want to fetch assets regardless of its type you need to apply assetType::any filter.
   *
   * Available filters:
   * categoryId, externalCategoryId, streamType, status, assetId, originAssetId, tag, story, assetType
   *
   * Behaviour: Specifying multiple filters results in logical AND between them.
   * Filters that specify the list (e.g. assetId) results in logical OR between list items.
   *
   * Invalid and unsupported filters result in 400 Bad Request.
   *
   * categoryId filter allows to get assets that belong to chosen category and all the children subcategories.
   *
   * Example:
   * categoryId::139|streamType::vod|status::active|assetId::2
   *
   * List example:
   * status::active,inactive,draft|assetId::2,52,56|tag::foo,bar
   */
  filters?: SvpFilters;

  /**
   * Additional fields:
   * settings, externalId, externalCategoryId, chapters, featuredChapters, url,
   * cuePoints, imageCaption, nextAsset, subtitles, sourceFiles, tags, stories, access, metadata
   */
  additional?: string;

  /**
   * Specifies the window of time to evaluate: hour, day, week, month
   * Default interval: day.
   */
  interval?: 'hour' | 'day' | 'week' | 'month';
}

function createGetMostSeenAssets(client: KyInstance, appName: string) {
  return async function getMostSeenAssets<T extends SvpAsset = SvpAsset>(
    {
      vendor,
      limit,
      page,
      filters,
      additional,
      interval,
    }: GetMostSeenAssetsArgs,
    options?: Options
  ) {
    const searchParams = new URLSearchParams({ appName });

    if (interval) {
      searchParams.set('interval', interval);
    }

    if (limit) {
      searchParams.set('limit', limit.toString(10));
    }

    if (page) {
      searchParams.set('page', page.toString(10));
    }

    if (filters && !filters.isEmpty()) {
      searchParams.set('filter', filters.toString());
    }

    if (additional) {
      searchParams.set('additional', additional);
    }

    return await client
      .get(`${vendor}/assets/most-seen`, {
        ...options,
        searchParams,
      })
      .json<HalResult<T>>();
  };
}

export interface GetCategoryArgs extends CommonArgs {
  categoryId: SvpCategoryId;
  additional?: boolean;
}

function createGetCategory(client: KyInstance, appName: string) {
  return async function getCategory<T extends SvpCategory = SvpCategory>(
    { vendor, categoryId, additional }: GetCategoryArgs,
    options?: Options
  ) {
    const searchParams = new URLSearchParams({ appName });
    if (additional !== undefined) {
      searchParams.set('additional', additional ? 'true' : 'false');
    }

    return await client
      .get(`${vendor}/categories/${categoryId}`, { ...options, searchParams })
      .json<T>();
  };
}

export interface GetCategoriesArgs extends CommonArgs {
  /**
   * Filter list in the format of key::value. Each filter has to be separated by | sign.
   *
   * Available filters:
   * parentId, isSeries, showCategory, shortPath
   *
   * Example:
   * parentId::172|isSeries::true|shortPath::foo
   */
  filters?: SvpFilters;

  additional?: boolean;
}

function createGetCategories(client: KyInstance, appName: string) {
  return async function getCategories<T extends SvpCategory = SvpCategory>(
    { vendor, filters, additional }: GetCategoriesArgs,
    options?: Options
  ) {
    const searchParams = new URLSearchParams({ appName });

    if (additional !== undefined) {
      searchParams.set('additional', additional ? 'true' : 'false');
    }

    if (filters && !filters.isEmpty()) {
      searchParams.set('filter', filters.toString());
    }

    return await client
      .get(`${vendor}/categories`, { ...options, searchParams })
      .json<HalResult<T>>();
  };
}

export interface GetSeasonAssetsArgs extends CommonArgs {
  /**
   * ID of a category that needs to be fetched
   */
  categoryId: SvpCategoryId;

  /**
   * ID of a season that needs to be fetched
   */
  seasonNumber: number;

  /**
   * Available fields:
   * episodeNumber, created, updated, published
   *
   * Example:
   * episodeNumber|-published
   */
  sort?: string;

  /**
   * Default: 10
   * Max: 100
   */
  limit?: number;

  /**
   * Page token returned by the previous API request that enables you to page through results.
   * Value of this field is presented in the "next" link of the response body.
   */
  page?: number;

  /**
   * Additional fields:
   * settings, externalId, externalCategoryId, chapters, featuredChapters, url,
   * cuePoints, imageCaption, nextAsset, subtitles, sourceFiles, tags, stories, access, metadata
   */
  additional?: string;
}

function createGetSeasonAssets(client: KyInstance, appName: string) {
  return async function getSeasonAssets<T extends SvpAsset = SvpAsset>(
    {
      vendor,
      categoryId,
      seasonNumber,
      sort,
      limit,
      page,
      additional,
    }: GetSeasonAssetsArgs,
    options?: Options
  ) {
    const searchParams = new URLSearchParams({ appName });

    if (sort) {
      searchParams.set('sort', sort);
    }

    if (limit) {
      searchParams.set('limit', limit.toString(10));
    }

    if (page) {
      searchParams.set('page', page.toString(10));
    }

    if (additional) {
      searchParams.set('additional', additional);
    }

    const { assets, _links } = await client
      .get(
        `${vendor}/categories/${categoryId}/seasons/${seasonNumber}/assets`,
        {
          ...options,
          searchParams,
        }
      )
      .json<HalLink & { assets: T[] }>();

    if (!assets.length) {
      return {
        _links,
        _embedded: { assets },
        total_items: assets.length,
      };
    }

    const params = new URLSearchParams({
      appName,
      limit: String(assets.length),
      filter: `id::${assets.map((asset) => asset.id).join(',')}`,
    });

    if (additional) {
      params.set('additional', additional);
    }

    const { _embedded, total_items } = await client
      .get(`${vendor}/search`, { searchParams: params })
      .json<HalResult<T>>();

    // assets returned from /search endpoint are not sorted correctly
    const sortedAssets = assets
      .map(({ id }) =>
        (_embedded.assets as T[]).find((asset) => asset.id === id)
      )
      .filter(isTruthy);

    return {
      _links,
      _embedded: { assets: sortedAssets },
      total_items,
    };
  };
}

export function createSvpApi(client: KyInstance, appName: string) {
  return {
    getAsset: createGetAsset(client, appName),
    getAssets: createGetAssets(client, appName),
    searchAssets: createSearchAssets(client, appName),
    getMostSeenAssets: createGetMostSeenAssets(client, appName),

    getCategory: createGetCategory(client, appName),
    getCategories: createGetCategories(client, appName),

    getSeasonAssets: createGetSeasonAssets(client, appName),
  };
}
